import React, { useState } from 'react';
import { sampleData } from '../../../../../DummyData/ConvertSampleJson';

// Define types for our component
interface JsonToAvroProps {}

// Avro schema type definition
interface AvroField {
  name: string;
  type: string | string[] | AvroComplexType;
  default?: any;
}

interface AvroComplexType {
  type: string;
  name?: string;
  items?: string | string[] | AvroComplexType;
  fields?: AvroField[];
  symbols?: string[];
  values?: string | AvroComplexType;
}

interface AvroSchema {
  type: string;
  name?: string;
  namespace?: string;
  fields?: AvroField[];
  items?: string | string[] | AvroComplexType;
  symbols?: string[];
  values?: string | AvroComplexType;
  doc?: string;
  aliases?: string[];
}

const JsonToAvro: React.FC<JsonToAvroProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the Avro schema output
  const [avroSchema, setAvroSchema] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the schema name
  const [schemaName, setSchemaName] = useState<string>('MyRecord');
  // State for the namespace
  const [namespace, setNamespace] = useState<string>('com.example');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isAvroGenerated, setIsAvroGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for schema complexity
  const [schemaComplexity, setSchemaComplexity] = useState<'simple' | 'detailed'>('detailed');
  // State for whether to include documentation
  const [includeDoc, setIncludeDoc] = useState<boolean>(true);
  // Optional documentation text
  const [docText, setDocText] = useState<string>('');

  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsAvroGenerated(false);
  };

  // Handle schema name change
  const handleSchemaNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSchemaName(e.target.value);
  };

  // Handle namespace change
  const handleNamespaceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNamespace(e.target.value);
  };

  // Handle documentation text change
  const handleDocTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDocText(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(sampleData, null, 2));
    setError('');
    setIsAvroGenerated(false);
    setAvroSchema('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setAvroSchema('');
    setError('');
    setSchemaName('MyRecord');
    setNamespace('com.example');
    setDocText('');
    setIsAvroGenerated(false);
  };

  // Infer Avro type from JavaScript value
  const inferAvroType = (value: any, fieldPath: string = ''): string | string[] | AvroComplexType => {
    if (value === null || value === undefined) {
      return ["null", "string"];
    }
    
    const typeOf = typeof value;
    
    if (typeOf === 'string') {
      return 'string';
    }
    
    if (typeOf === 'number') {
      // Check if the number is an integer
      return Number.isInteger(value) ? 'int' : 'double';
    }
    
    if (typeOf === 'boolean') {
      return 'boolean';
    }
    
    if (Array.isArray(value)) {
      if (value.length === 0) {
        // Empty array, default to array of strings
        return {
          type: 'array',
          items: 'string'
        };
      }
      
      // For simple schema, just use the type of the first element
      if (schemaComplexity === 'simple') {
        return {
          type: 'array',
          items: inferAvroType(value[0], `${fieldPath}[0]`)
        };
      }
      
      // For detailed schema, try to infer a union type if array elements have different types
      const elementTypes = value.map((item, index) => 
        inferAvroType(item, `${fieldPath}[${index}]`)
      );
      
      // Check if all element types are the same
      const firstType = JSON.stringify(elementTypes[0]);
      const allSameType = elementTypes.every(type => 
        JSON.stringify(type) === firstType
      );
      
      if (allSameType) {
        return {
          type: 'array',
          items: elementTypes[0]
        };
      } else {
        // Create a union type for array items
        const uniqueTypes = new Set(elementTypes.map(item => 
          typeof item === 'string' ? item : JSON.stringify(item)
        ));
        
        const unionTypes = Array.from(uniqueTypes).map(item => 
          typeof item === 'string' ? item : JSON.parse(item)
        );
        
        return {
          type: 'array',
          items: unionTypes
        };
      }
    }
    
    if (typeOf === 'object') {
      // Create a nested record type for objects
      const recordName = fieldPath ? 
        `${schemaName}_${fieldPath.replace(/[^a-zA-Z0-9]/g, '_')}` : 
        `${schemaName}_nested`;
        
      const fields: AvroField[] = Object.entries(value).map(([key, val]) => ({
        name: key,
        type: inferAvroType(val, `${fieldPath ? fieldPath + '.' : ''}${key}`)
      }));
      
      const recordType: AvroComplexType = {
        type: 'record',
        fields
      };
      
      // Only add name to top-level records or when in detailed mode
      if (schemaComplexity === 'detailed' || !fieldPath) {
        recordType.name = recordName;
      }
      
      return recordType;
    }
    
    // Default fallback
    return 'string';
  };

  // Generate Avro schema from JSON
  const generateAvroSchema = (jsonObj: any): AvroSchema => {
    const baseSchema: AvroSchema = {
      type: 'record',
      name: schemaName,
      namespace: namespace
    };
    
    if (includeDoc && docText) {
      baseSchema.doc = docText;
    }
    
    if (Array.isArray(jsonObj)) {
      // If the root is an array, create an array schema
      return {
        type: 'array',
        items: inferAvroType(jsonObj[0] || {})
      };
    } else if (typeof jsonObj === 'object' && jsonObj !== null) {
      // If the root is an object, create a record schema
      const fields = Object.entries(jsonObj).map(([key, value]) => ({
        name: key,
        type: inferAvroType(value, key)
      }));
      
      return {
        ...baseSchema,
        fields
      };
    } else {
      // If the root is a primitive type, create a simple schema
      return {
        type: inferAvroType(jsonObj) as string
      };
    }
  };

  // Convert JSON to Avro
  const convertJsonToAvro = () => {
    setError('');
    setIsLoading(true);
    setIsAvroGenerated(false);
    setAvroSchema('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      const schema = generateAvroSchema(jsonData);
      
      setAvroSchema(JSON.stringify(schema, null, 2));
      setIsAvroGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating Avro schema: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy Avro schema to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(avroSchema)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download Avro schema file
  const downloadAvroSchema = () => {
    const blob = new Blob([avroSchema], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${schemaName.toLowerCase()}.avsc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to Avro Schema Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to Avro schema. Paste your JSON data below to automatically generate an Avro schema that matches your data structure.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="schemaName" className="block text-sm font-medium text-gray-700 mb-1">
                  Schema Name
                </label>
                <input
                  type="text"
                  id="schemaName"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={schemaName}
                  onChange={handleSchemaNameChange}
                  placeholder="Enter schema name"
                />
              </div>
              
              <div>
                <label htmlFor="namespace" className="block text-sm font-medium text-gray-700 mb-1">
                  Namespace
                </label>
                <input
                  type="text"
                  id="namespace"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={namespace}
                  onChange={handleNamespaceChange}
                  placeholder="e.g., com.example"
                />
              </div>
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="schemaComplexity" className="block text-sm font-medium text-gray-700 mb-1">
                    Schema Complexity
                  </label>
                  <select
                    id="schemaComplexity"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={schemaComplexity}
                    onChange={(e) => setSchemaComplexity(e.target.value as 'simple' | 'detailed')}
                  >
                    <option value="simple">Simple - Less precise but more readable</option>
                    <option value="detailed">Detailed - More precise type definitions</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Simple mode generates a more concise schema, while detailed mode handles mixed types and nested structures more precisely.
                  </p>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeDoc"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={includeDoc}
                    onChange={(e) => setIncludeDoc(e.target.checked)}
                  />
                  <label htmlFor="includeDoc" className="ml-2 block text-sm text-gray-700">
                    Include Documentation
                  </label>
                </div>
                
                {includeDoc && (
                  <div>
                    <label htmlFor="docText" className="block text-sm font-medium text-gray-700 mb-1">
                      Documentation Text
                    </label>
                    <textarea
                      id="docText"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      value={docText}
                      onChange={handleDocTextChange}
                      placeholder="Enter schema documentation"
                      rows={3}
                    />
                  </div>
                )}
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToAvro}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to Avro Schema'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              Avro Schema Output
            </label>
            
            {isAvroGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy Schema'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadAvroSchema}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download .avsc
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isAvroGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    Avro schema will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isAvroGenerated && (
              <div className="flex-grow flex flex-col">
                <textarea
                  className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                  value={avroSchema}
                  readOnly
                />
              </div>
            )}
          </div>
          
          {isAvroGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">Avro Schema Successfully Generated!</p>
              </div>
              <p className="text-sm">
                The generated schema can be used with Avro tools for data serialization. Download the .avsc file or copy the schema to your clipboard.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to Avro Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool analyzes your JSON data and automatically generates an Avro schema that matches your data structure. 
              It infers appropriate Avro types for each field, handles nested objects and arrays, and creates a schema file that 
              can be used with Avro tools for data serialization.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Automatic Type Inference</strong> - Detects appropriate Avro types for your JSON data</li>
              <li><strong>Nested Structure Support</strong> - Handles complex JSON with nested objects and arrays</li>
              <li><strong>Customizable Schema</strong> - Set schema name, namespace, and documentation</li>
              <li><strong>Complexity Options</strong> - Choose between simple and detailed schema generation</li>
              <li><strong>Direct Download</strong> - Save the generated schema as an .avsc file</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format JSON" button to properly format and validate your JSON data before conversion</li>
              <li>For complex data, use the "Detailed" schema complexity option for more precise type definitions</li>
              <li>Include documentation to provide context and information about your schema's purpose</li>
              <li>Make sure your JSON input has all the fields you want to include in your schema</li>
              <li>For arrays with mixed types, the converter will attempt to create appropriate union types</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">About Avro</h3>
            <p className="text-gray-600">
              Apache Avro is a data serialization system that provides rich data structures, a compact binary format, and 
              integration with many programming languages. Avro uses JSON for defining data types and protocols, making it 
              both human-readable and machine-friendly. It's commonly used in big data systems like Apache Kafka and Hadoop.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToAvro;